/**
 * The little markdown Ollie replies use — bold, italics, bullet and numbered
 * lists, line breaks — parsed into plain data. Rendered as React elements
 * only (never as HTML), so anything else in a reply stays literal text.
 *
 * Blocks: `{ type: 'paragraph', lines: Inline[][] }` or
 * `{ type: 'list', ordered, start, items: Inline[][] }`.
 * Inline: `{ type: 'text' | 'bold' | 'italic', value }` (bold may hold
 * `children` with italics).
 */

const BULLET_LINE = /^\s*[-*•]\s+(.*)$/
const NUMBERED_LINE = /^\s*(\d{1,3})[.)]\s+(.*)$/
const HEADING_LINE = /^\s{0,3}#{1,6}\s+(.*?)\s*#*\s*$/
const RULE_LINE = /^\s{0,3}([-*_])(\s*\1){2,}\s*$/
const INDENTED_LINE = /^\s{2,}\S/

const INLINE_PATTERN =
  /\*\*(\S(?:[\s\S]*?\S)??)\*\*|\*(\S(?:[^*\n]*?\S)?)\*|`([^`\n]+)`|\[([^\]\n]+)\]\(([^)\s]+)\)/g

/** Drop markers a partly revealed reply hasn't closed yet (typewriter). */
const stripDanglingMarkers = text => text.replace(/\*+/g, '')

const pushText = (tokens, value) => {
  if (!value) return
  const last = tokens[tokens.length - 1]
  if (last?.type === 'text') last.value += value
  else tokens.push({ type: 'text', value })
}

/**
 * @param {string} text
 * @param {{ partial?: boolean }} [options]
 * @returns {Array<{ type: string, value?: string, children?: object[] }>}
 */
export function parseOllieInline(text, { partial = false } = {}) {
  const source = String(text || '')
  const tokens = []
  const pattern = new RegExp(INLINE_PATTERN.source, 'g')
  let lastIndex = 0
  let match
  while ((match = pattern.exec(source)) !== null) {
    const before = source.slice(lastIndex, match.index)
    pushText(tokens, partial ? stripDanglingMarkers(before) : before)
    const [, bold, italic, code, linkLabel, linkUrl] = match
    if (bold !== undefined) {
      const children = parseOllieInline(bold, { partial })
      const plain = children.every(child => child.type === 'text')
      tokens.push(
        plain
          ? { type: 'bold', value: children.map(c => c.value).join('') }
          : { type: 'bold', value: bold, children },
      )
    } else if (italic !== undefined) {
      tokens.push({ type: 'italic', value: italic })
    } else if (code !== undefined) {
      pushText(tokens, code)
    } else {
      pushText(
        tokens,
        linkUrl.startsWith('/') ? `${linkLabel} ${linkUrl}` : linkLabel,
      )
    }
    lastIndex = match.index + match[0].length
  }
  const rest = source.slice(lastIndex)
  pushText(tokens, partial ? stripDanglingMarkers(rest) : rest)
  return tokens
}

/**
 * @param {string} content
 * @param {{ partial?: boolean }} [options] `partial` while the typewriter
 *   is still revealing: unclosed `*` / `**` are hidden, not shown raw.
 */
export function parseOllieMarkdown(content, options = {}) {
  const lines = String(content || '')
    .replace(/\r\n?/g, '\n')
    .split('\n')
  const blocks = []
  let paragraph = null
  let list = null

  const inline = text => parseOllieInline(text, options)
  const closeParagraph = () => {
    if (paragraph) blocks.push(paragraph)
    paragraph = null
  }
  const closeList = () => {
    if (list) blocks.push(list)
    list = null
  }

  lines.forEach(line => {
    if (!line.trim() || RULE_LINE.test(line)) {
      closeParagraph()
      closeList()
      return
    }

    const numbered = line.match(NUMBERED_LINE)
    const bullet = numbered ? null : line.match(BULLET_LINE)
    if (numbered || bullet) {
      const ordered = Boolean(numbered)
      closeParagraph()
      if (!list || list.ordered !== ordered) {
        closeList()
        list = {
          type: 'list',
          ordered,
          start: ordered ? Number(numbered[1]) : 1,
          items: [],
        }
      }
      list.items.push(inline(ordered ? numbered[2] : bullet[1]))
      return
    }

    if (list && INDENTED_LINE.test(line)) {
      const item = list.items[list.items.length - 1]
      item.push({ type: 'text', value: ' ' }, ...inline(line.trim()))
      return
    }

    closeList()
    const heading = line.match(HEADING_LINE)
    if (heading) {
      closeParagraph()
      blocks.push({
        type: 'paragraph',
        lines: [[{ type: 'bold', value: heading[1] }]],
      })
      return
    }
    if (!paragraph) paragraph = { type: 'paragraph', lines: [] }
    paragraph.lines.push(inline(line.trim()))
  })

  closeParagraph()
  closeList()
  return blocks
}

const inlineToPlain = tokens =>
  tokens
    .map(token =>
      token.children ? inlineToPlain(token.children) : token.value || '',
    )
    .join('')

/** Copy button: the reply as the chef reads it, without the markers. */
export function ollieMarkdownToPlainText(content) {
  return parseOllieMarkdown(content)
    .map(block =>
      block.type === 'list'
        ? block.items
            .map((item, index) =>
              block.ordered
                ? `${block.start + index}. ${inlineToPlain(item)}`
                : `• ${inlineToPlain(item)}`,
            )
            .join('\n')
        : block.lines.map(inlineToPlain).join('\n'),
    )
    .join('\n\n')
}
